import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  RefreshControl,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import {
  ModuleHeader,
  BottomTabBar,
  type BottomTabItem,
  FriendList,
  type FriendListItem,
  type FriendListStrings,
  type FriendTagOption,
  AddFriendModal,
  type AddFriendModalStrings,
  type AddFriendSearchResult,
  Card,
} from '@cubemeet/ui';
import type { AppDispatch, RootState } from '../../store';
import { useI18n } from '../../i18n';
import { useFriendsTranslation, getTranslatedPresetTags } from './useFriendsTranslation';
import { FriendTagsAPI } from '@cubemeet/core/services/friendTagsAPI';
import {
  getFriends,
  sendFriendRequest,
  searchUsers,
  clearSearchResults,
  removeFriend,
} from '@cubemeet/core/store/slices/friendsSlice';
import { PRESET_FRIEND_TAGS } from '@cubemeet/core/types/friends';
import type { FriendWithTags } from '@cubemeet/core/types/friends';

// Minimal local type declarations to match expected shapes used in this file.
// These are intentionally conservative; if there's a canonical shared type in
// another package we should prefer importing that instead.
type StatusTone = 'info' | 'warning' | 'error' | 'success';

interface TagEditorStrings {
  cancel: string;
  title: string;
  saving: string;
  save: string;
  selectedTags: string;
  noTagsSelected: string;
  presetTags: string;
  myTags: string;
  addCustomTag: string;
  tagInputPlaceholder: string;
  tagAlreadyExists: string;
  tagHelp: string;
  helpText: string;
}

interface TagEditorModalProps {
  visible: boolean;
  friendName: string;
  initialTags: string[];
  presetTagValues: string[];
  presetTagLabels: Record<string, string>;
  userTags: string[];
  strings: TagEditorStrings;
  loading?: boolean;
  onClose: () => void;
  onSave: (tags: string[]) => void;
  onNotify: (message: string, tone?: StatusTone) => void;
}

const TagEditorModal: React.FC<TagEditorModalProps> = ({
  visible,
  friendName,
  initialTags,
  presetTagValues,
  presetTagLabels,
  userTags,
  strings,
  loading,
  onClose,
  onSave,
  onNotify,
}) => {
  const [selectedTags, setSelectedTags] = useState<string[]>(initialTags);
  const [customTag, setCustomTag] = useState('');

  useEffect(() => {
    if (visible) {
      setSelectedTags(initialTags);
      setCustomTag('');
    }
  }, [visible, initialTags]);

  const toggleTag = useCallback((tag: string) => {
    setSelectedTags((prev) => {
      return prev.includes(tag) ? prev.filter((item) => item !== tag) : [...prev, tag];
    });
  }, []);

  const handleAddCustomTag = useCallback(() => {
    const trimmed = customTag.trim();
    if (!trimmed) {
      return;
    }
    if (selectedTags.includes(trimmed)) {
      onNotify(strings.tagAlreadyExists, 'warning');
      return;
    }
    setSelectedTags((prev) => [...prev, trimmed]);
    setCustomTag('');
  }, [customTag, selectedTags, strings.tagAlreadyExists, onNotify]);

  const handleSubmit = useCallback(() => {
    const trimmed = customTag.trim();
    const nextTags = trimmed && !selectedTags.includes(trimmed)
      ? [...selectedTags, trimmed]
      : selectedTags;
    onSave(nextTags);
  }, [customTag, selectedTags, onSave]);

  const customOnlyTags = useMemo(() => {
    const presetSet = new Set(presetTagValues);
    return Array.from(new Set(userTags.filter((tag) => !presetSet.has(tag))));
  }, [userTags, presetTagValues]);

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose}>
      <View style={tagStyles.container}>
        <View style={tagStyles.header}>
          <TouchableOpacity style={tagStyles.headerButton} onPress={onClose}>
            <Text style={tagStyles.headerButtonText}>{strings.cancel}</Text>
          </TouchableOpacity>
          <Text style={tagStyles.headerTitle}>{strings.title}</Text>
          <TouchableOpacity
            style={[tagStyles.headerButton, tagStyles.saveButton, loading && tagStyles.saveButtonDisabled]}
            onPress={handleSubmit}
            disabled={loading}
          >
            <Text style={tagStyles.saveButtonText}>{loading ? strings.saving : strings.save}</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={tagStyles.scroll} contentContainerStyle={tagStyles.scrollContent}>
          <View style={tagStyles.section}>
            <Text style={tagStyles.sectionTitle}>{friendName}</Text>
          </View>

          <View style={tagStyles.section}>
            <Text style={tagStyles.sectionSubtitle}>
              {strings.selectedTags} ({selectedTags.length})
            </Text>
            <View style={tagStyles.tagGrid}>
              {selectedTags.length === 0 ? (
                <Text style={tagStyles.emptyText}>{strings.noTagsSelected}</Text>
              ) : (
                selectedTags.map((tag) => (
                  <TouchableOpacity
                    key={`selected-${tag}`}
                    style={[tagStyles.tagChip, tagStyles.selectedChip]}
                    onPress={() => toggleTag(tag)}
                  >
                    <Text style={tagStyles.selectedChipText}>{presetTagLabels[tag] || tag}</Text>
                    <Text style={tagStyles.removeIcon}>×</Text>
                  </TouchableOpacity>
                ))
              )}
            </View>
          </View>

          <View style={tagStyles.section}>
            <Text style={tagStyles.sectionSubtitle}>{strings.presetTags}</Text>
            <View style={tagStyles.tagGrid}>
              {presetTagValues.map((tag) => {
                const active = selectedTags.includes(tag);
                return (
                  <TouchableOpacity
                    key={`preset-${tag}`}
                    style={[tagStyles.tagChip, active ? tagStyles.selectedChip : tagStyles.unselectedChip]}
                    onPress={() => toggleTag(tag)}
                  >
                    <Text style={active ? tagStyles.selectedChipText : tagStyles.unselectedChipText}>
                      {presetTagLabels[tag] || tag}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          {customOnlyTags.length > 0 && (
            <View style={tagStyles.section}>
              <Text style={tagStyles.sectionSubtitle}>{strings.myTags}</Text>
              <View style={tagStyles.tagGrid}>
                {customOnlyTags.map((tag) => {
                  const active = selectedTags.includes(tag);
                  return (
                    <TouchableOpacity
                      key={`user-${tag}`}
                      style={[tagStyles.tagChip, active ? tagStyles.selectedChip : tagStyles.unselectedChip]}
                      onPress={() => toggleTag(tag)}
                    >
                      <Text style={active ? tagStyles.selectedChipText : tagStyles.unselectedChipText}>{tag}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          )}

          <View style={tagStyles.section}>
            <Text style={tagStyles.sectionSubtitle}>{strings.addCustomTag}</Text>
            <View style={tagStyles.customTagRow}>
              <TextInput
                value={customTag}
                onChangeText={setCustomTag}
                placeholder={strings.tagInputPlaceholder}
                style={tagStyles.textInput}
              />
              <TouchableOpacity
                style={[tagStyles.addButton, !customTag.trim() && tagStyles.addButtonDisabled]}
                onPress={handleAddCustomTag}
                disabled={!customTag.trim()}
              >
                <Text style={tagStyles.addButtonText}>＋</Text>
              </TouchableOpacity>
            </View>
          </View>

          <View style={tagStyles.section}>
            <Text style={tagStyles.sectionSubtitle}>{strings.tagHelp}</Text>
            <Text style={tagStyles.helpText}>{strings.helpText}</Text>
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
};

const FriendsModule: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const authState = useSelector((state: RootState) => (state as any)?.auth ?? {}) as any;
  const friendsState = useSelector((state: RootState) => (state as any)?.friends ?? {}) as any;
  const user = authState?.user;

  const { translations } = useI18n();
  const { t: friendsT, language } = useFriendsTranslation();

  const [friendsWithTags, setFriendsWithTags] = useState<FriendWithTags[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [showAddFriend, setShowAddFriend] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [friendMessage, setFriendMessage] = useState('');
  const [selectedFriendId, setSelectedFriendId] = useState<string | null>(null);
  const [tagFilter, setTagFilter] = useState<string>('all');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [editingFriend, setEditingFriend] = useState<{ id: string; name: string; tags: string[] } | null>(null);
  const [tagEditorVisible, setTagEditorVisible] = useState(false);
  const [tagEditorLoading, setTagEditorLoading] = useState(false);
  const [userTags, setUserTags] = useState<string[]>([]);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusTone, setStatusTone] = useState<StatusTone>('info');

  const { friends = [], searchResults = [], loading = false, searchLoading = false } = friendsState;

  const commonT = useMemo(() => {
    return (key: string) => {
      if (!translations) return key;
      return key.split('.').reduce<any>((acc, part) => {
        if (acc && typeof acc === 'object' && part in acc) {
          return acc[part];
        }
        return null;
      }, translations) ?? key;
    };
  }, [translations]);

  const translatedPresetTags = useMemo(() => getTranslatedPresetTags(language), [language]);

  const presetTagMapping = useMemo(() => {
    return PRESET_FRIEND_TAGS.reduce((acc, originalTag, index) => {
      acc[originalTag] = translatedPresetTags[index] ?? originalTag;
      return acc;
    }, {} as Record<string, string>);
  }, [translatedPresetTags]);

  const showStatus = useCallback((message: string, tone: StatusTone = 'info') => {
    setStatusMessage(message);
    setStatusTone(tone);
    if (tone === 'error') {
      console.error(message);
    }
  }, []);

  useEffect(() => {
    if (!statusMessage) return;
    const timer = setTimeout(() => setStatusMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [statusMessage]);

  const computeAvailableTags = useCallback((items: FriendWithTags[]) => {
    const tagSet = new Set<string>();
    items.forEach((friend) => {
      (friend.tags || []).forEach((tag: string) => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, []);

  const fetchFriendsWithTags = useCallback(async () => {
    const data = await FriendTagsAPI.getFriendsWithTags();
    setFriendsWithTags(data);
    setAvailableTags(computeAvailableTags(data));
  }, [computeAvailableTags]);

  const loadData = useCallback(async (isUserTriggered = false) => {
    if (!user?.id) return;
    if (isUserTriggered) {
      setIsRefreshing(true);
    } else {
      setInitializing(true);
    }

    try {
      const results = await Promise.allSettled([
        dispatch(getFriends(user.id)).unwrap(),
        fetchFriendsWithTags(),
      ]);

      const rejected = results.find((item) => item.status === 'rejected');
      if (rejected && rejected.status === 'rejected') {
        const reason = (rejected.reason as any) ?? '';
        const message = typeof reason === 'string' ? reason : reason?.message;
        showStatus(
          message ? `${friendsT('friends.loadFailed')}: ${message}` : friendsT('friends.loadFailed'),
          'error',
        );
      }
    } finally {
      if (isUserTriggered) {
        setIsRefreshing(false);
      }
      setInitializing(false);
    }
  }, [dispatch, user?.id, fetchFriendsWithTags, friendsT, showStatus]);

  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [user?.id, loadData]);

  useEffect(() => {
    if (!user?.id) {
      setInitializing(false);
    }
  }, [user?.id]);

  const getTagDisplayText = useCallback((tag: string) => {
    return presetTagMapping[tag] || tag;
  }, [presetTagMapping]);

  const friendListItems = useMemo<FriendListItem[]>(() => {
    if (!friends || friends.length === 0) {
      return [];
    }

    return friends.map((friend: any) => {
      const friendTags = friendsWithTags.find((item) => item.friend_id === friend.friend_id);
      const translatedTags = friendTags?.tags?.map((tag: string) => getTagDisplayText(tag)) ?? [];

      return {
        id: friend.id,
        friendUserId: friend.friend_id,
        displayName: friend.profiles?.display_name || friendsT('friends.unknownUser'),
        avatarUrl: friend.profiles?.avatar_url,
        bio: friend.profiles?.bio,
        tags: translatedTags,
        tagKeys: friendTags?.tags ?? [],
        raw: friend,
      } satisfies FriendListItem;
    });
  }, [friends, friendsWithTags, getTagDisplayText, friendsT]);

  const tagOptions = useMemo<FriendTagOption[]>(() => {
    const options: FriendTagOption[] = [
      {
        value: 'all',
        label: `${friendsT('friends.allFriends')} (${friends.length})`,
        count: friends.length,
      },
    ];

    availableTags.forEach((tag) => {
      const count = friends.reduce((acc: number, friend: any) => {
        const friendTags = friendsWithTags.find((item) => item.friend_id === friend.friend_id);
        if (friendTags?.tags.includes(tag)) {
          return acc + 1;
        }
        return acc;
      }, 0);

      options.push({
        value: tag,
        label: `${getTagDisplayText(tag)} (${count})`,
        count,
      });
    });

    return options;
  }, [availableTags, friends, friendsWithTags, friendsT, getTagDisplayText]);

  const friendListStrings = useMemo<FriendListStrings>(() => {
    const tagLabel = tagFilter === 'all' ? '' : getTagDisplayText(tagFilter);

    return {
      emptyAllTitle: friendsT('friends.noFriends'),
      emptyAllSubtitle: friendsT('friends.addFriendsHint'),
      emptyTagTitle: friendsT('friends.noTaggedFriends', { tag: tagLabel || tagFilter }),
      emptyTagSubtitle: friendsT('friends.tryOtherTags'),
      addFriendButton: friendsT('friends.addFriend'),
      editTagsA11y: friendsT('friends.editTags'),
      deleteFriendA11y: friendsT('friends.removeFriend'),
    };
  }, [friendsT, tagFilter, getTagDisplayText]);

  const filteredFriendListItems = useMemo(() => {
    if (tagFilter === 'all') {
      return friendListItems;
    }
    return friendListItems.filter((friend) => friend.tagKeys?.includes(tagFilter));
  }, [friendListItems, tagFilter]);

  const addFriendModalStrings = useMemo<AddFriendModalStrings>(() => ({
    title: friendsT('friends.addFriend'),
    contactLabel: friendsT('friends.contactLabel'),
    contactPlaceholder: friendsT('friends.contactPlaceholder'),
    searchHint: friendsT('friends.searchHint'),
    searching: friendsT('friends.searching'),
    searchResults: friendsT('friends.searchResults'),
    messageLabel: friendsT('friends.messageLabel'),
    messagePlaceholder: friendsT('friends.messagePlaceholder'),
    confirmLabel: friendsT('friends.sendRequest'),
    cancelLabel: commonT('common.cancel'),
    unknownUser: friendsT('friends.unknownUser'),
  }), [friendsT, commonT]);

  const addFriendSearchResults = useMemo<AddFriendSearchResult[]>(() => {
    if (!searchResults || searchResults.length === 0) {
      return [];
    }

    return searchResults.map((user: any) => ({
      id: user.id,
      displayName: user.display_name,
      email: user.email,
      phone: user.phone,
      username: user.username,
      contact: user.email || user.phone || user.username || null,
      avatarUrl: user.avatar_url,
      bio: user.bio,
    }));
  }, [searchResults]);

  const handleSearchInputChange = useCallback((value: string) => {
    setSearchQuery(value);
    const trimmed = value.trim();
    if (!trimmed) {
      dispatch(clearSearchResults());
      return;
    }
    dispatch(searchUsers({ query: trimmed }))
      .unwrap()
      .catch((error: any) => {
        showStatus(error || friendsT('friends.searchFailed'), 'error');
      });
  }, [dispatch, friendsT, showStatus]);

  const handleSendFriendRequest = useCallback(async (targetUser?: AddFriendSearchResult) => {
    let requestData: any = {
      message: friendMessage.trim() || undefined,
    };

    if (targetUser) {
      if (targetUser.email) {
        requestData.friendEmail = targetUser.email;
      } else if (targetUser.phone) {
        requestData.friendPhone = targetUser.phone;
      } else if (targetUser.username) {
        requestData.friendUsername = targetUser.username;
      } else {
        showStatus(friendsT('friends.incompleteUserInfo'), 'warning');
        return;
      }
    } else {
      const query = searchQuery.trim();
      if (!query) {
        showStatus(friendsT('friends.enterContactInfo'), 'warning');
        return;
      }
      if (query.includes('@')) {
        requestData.friendEmail = query;
      } else if (/^[0-9+\-\s()]{7,20}$/.test(query)) {
        requestData.friendPhone = query;
      } else {
        requestData.friendUsername = query;
      }
    }

    try {
      await dispatch(sendFriendRequest(requestData)).unwrap();
      showStatus(friendsT('friends.requestSent'), 'success');
      handleCloseAddFriendModal();
      loadData();
    } catch (error: any) {
      showStatus(error || friendsT('friends.requestFailed'), 'error');
    }
  }, [dispatch, friendMessage, friendsT, searchQuery, loadData, showStatus]);

  const handleRemoveFriend = useCallback(async (friendId: string, friendName: string) => {
    setDeleteLoading(true);
    try {
      await dispatch(removeFriend({ friendId })).unwrap();
      showStatus(friendsT('friends.removedFriend', { name: friendName }), 'success');
      setSelectedFriendId(null);
      fetchFriendsWithTags();
    } catch (error: any) {
      showStatus(error || friendsT('friends.removeFailed'), 'error');
    } finally {
      setDeleteLoading(false);
    }
  }, [dispatch, friendsT, fetchFriendsWithTags, showStatus]);

  const handleCloseAddFriendModal = useCallback(() => {
    setShowAddFriend(false);
    setSearchQuery('');
    setFriendMessage('');
    dispatch(clearSearchResults());
  }, [dispatch]);

  const handleFriendPress = useCallback((friend: FriendListItem) => {
    if (selectedFriendId === friend.id) {
      setSelectedFriendId(null);
      return;
    }

    if (selectedFriendId === null) {
      setSelectedFriendId(friend.id);
    }
  }, [selectedFriendId]);

  const handleFriendLongPress = useCallback((friend: FriendListItem) => {
    setSelectedFriendId(friend.id);
  }, []);

  const openTagEditor = useCallback(async (friend: FriendListItem) => {
  const friendRecord = friendsWithTags.find((item) => item.friend_id === friend.friendUserId);
    setEditingFriend({
      id: friend.friendUserId,
      name: friend.displayName || friendsT('friends.unknownUser'),
      tags: friendRecord?.tags || [],
    });
    setTagEditorVisible(true);
    setSelectedFriendId(null);
    try {
      const tags = await FriendTagsAPI.getAllUserTags();
      setUserTags(tags);
    } catch (error) {
      showStatus(friendsT('friends.tagSaveError') + (error ? `: ${error}` : ''), 'error');
    }
  }, [friendsWithTags, friendsT, showStatus]);

  const handleTagSave = useCallback(async (tags: string[]) => {
    if (!editingFriend) return;
    setTagEditorLoading(true);
    try {
      await FriendTagsAPI.updateFriendTags({ friend_id: editingFriend.id, tags });
      showStatus(friendsT('friends.tagsUpdated'), 'success');
      setFriendsWithTags((prev) => {
        const next = prev.map((friend) => (
          friend.friend_id === editingFriend.id ? { ...friend, tags } : friend
        ));
        setAvailableTags(computeAvailableTags(next));
        return next;
      });
      setUserTags((prev) => Array.from(new Set([...prev, ...tags])));
      setTagEditorVisible(false);
      setEditingFriend(null);
    } catch (error: any) {
      showStatus(error || friendsT('friends.tagSaveError'), 'error');
    } finally {
      setTagEditorLoading(false);
    }
  }, [editingFriend, friendsT, computeAvailableTags, showStatus]);

  const tagEditorStrings = useMemo<TagEditorStrings>(() => ({
    title: friendsT('friends.editTags'),
    selectedTags: friendsT('friends.selectedTags'),
    presetTags: friendsT('friends.presetTags'),
    myTags: friendsT('friends.myTags'),
    addCustomTag: friendsT('friends.addCustomTag'),
    tagInputPlaceholder: friendsT('friends.tagInputPlaceholder'),
    tagHelp: friendsT('friends.tagHelp'),
    helpText: friendsT('friends.tagHelpText'),
    noTagsSelected: friendsT('friends.noTagsSelected'),
    save: friendsT('friends.save'),
    saving: friendsT('friends.saving'),
    cancel: commonT('common.cancel'),
    tagAlreadyExists: friendsT('friends.tagAlreadyExists'),
  }), [friendsT, commonT]);

  const items: BottomTabItem[] = useMemo(() => ([
    { key: 'settings', label: 'Settings', icon: 'settings' },
    { key: 'notifications', label: 'Notifications', icon: 'notifications' },
    { key: 'hub', label: '', icon: 'apps' },
    { key: 'profile', label: 'Profile', icon: 'person' },
    { key: 'friends', label: 'Friends', icon: 'people' },
  ]), []);

  const onTabPress = useCallback((_: number, key: string) => {
    const map: Record<string, string> = {
      settings: 'dashboard',
      notifications: 'notifications',
      hub: 'dashboard',
      profile: 'profile',
      friends: 'friends',
    };
    const target = map[key] || 'dashboard';
    if (typeof window !== 'undefined') {
      window.location.hash = target;
    }
  }, []);

  const onCenterPress = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.location.hash = 'dashboard';
    }
  }, []);

  return (
    <SafeAreaView style={styles.safeArea}>
      {/* Full-width header and content */}
      <ModuleHeader
        moduleType="dashboard"
        title={friendsT('friends.title')}
        subtitle={friendsT('friends.subtitle')}
        showBackButton={false}
        showCreateButton
        onCreatePress={() => setShowAddFriend(true)}
      />

      {initializing ? (
        <View style={styles.centeredState}>
          <ActivityIndicator size="small" color="#2563EB" />
          <Text style={styles.centeredText}>{friendsT('friends.loading.title')}</Text>
          <Text style={styles.centeredSubtext}>{friendsT('friends.loading.subtitle')}</Text>
        </View>
      ) : (
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.content}
          refreshControl={(
            <RefreshControl refreshing={isRefreshing} onRefresh={() => loadData(true)} />
          )}
        >
          {statusMessage && (
            <Card style={[styles.statusCard, styles[`status_${statusTone}` as const]]}>
              <Text style={styles.statusText}>{statusMessage}</Text>
            </Card>
          )}

          <FriendList
            friends={filteredFriendListItems}
            tagOptions={tagOptions}
            tagFilter={tagFilter}
            selectedFriendId={selectedFriendId}
            deleteLoading={deleteLoading}
            showAddFriendCta={tagFilter === 'all'}
            strings={friendListStrings}
            onAddFriendPress={() => setShowAddFriend(true)}
            onTagFilterChange={(value) => setTagFilter(value)}
            onFriendPress={handleFriendPress}
            onFriendLongPress={handleFriendLongPress}
            onEditFriendTags={openTagEditor}
            onRemoveFriend={(friend) => handleRemoveFriend(friend.friendUserId, friend.displayName)}
          />
        </ScrollView>
      )}

      <AddFriendModal
        visible={showAddFriend}
        searchQuery={searchQuery}
        friendMessage={friendMessage}
        searchResults={addFriendSearchResults}
        strings={addFriendModalStrings}
        searchLoading={searchLoading}
        submitting={loading}
        onClose={handleCloseAddFriendModal}
        onSearchQueryChange={handleSearchInputChange}
        onSubmitManual={() => handleSendFriendRequest()}
        onSelectSearchResult={(user) => handleSendFriendRequest(user)}
        onFriendMessageChange={setFriendMessage}
      />

      {editingFriend && (
        <TagEditorModal
          visible={tagEditorVisible}
          friendName={editingFriend.name}
          initialTags={editingFriend.tags}
          presetTagValues={Array.from(PRESET_FRIEND_TAGS)}
          presetTagLabels={presetTagMapping}
          userTags={userTags}
          strings={tagEditorStrings}
          loading={tagEditorLoading}
          onClose={() => {
            setTagEditorVisible(false);
            setEditingFriend(null);
          }}
          onSave={handleTagSave}
          onNotify={showStatus}
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#F3F4F6', paddingHorizontal: 0, paddingVertical: 0 },
  scroll: { flex: 1 },
  content: { paddingHorizontal: 16, paddingVertical: 24, paddingBottom: 24 },
  bottomBarArea: { borderTopWidth: 1, borderTopColor: '#E5E7EB', paddingHorizontal: 24, paddingTop: 6, paddingBottom: 8, backgroundColor: '#FFFFFF', borderBottomLeftRadius: 28, borderBottomRightRadius: 28 },
  centeredState: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32 },
  centeredText: { marginTop: 16, fontSize: 16, color: '#1F2937', fontWeight: '600' },
  centeredSubtext: { marginTop: 8, fontSize: 14, color: '#6B7280' },
  statusCard: { padding: 16 },
  statusText: { fontSize: 14 },
  status_info: { backgroundColor: '#E0F2FE' },
  status_success: { backgroundColor: '#DCFCE7' },
  status_warning: { backgroundColor: '#FEF3C7' },
  status_error: { backgroundColor: '#FEE2E2' },
});

const tagStyles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F8FAFC' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  headerButton: { padding: 8 },
  headerButtonText: { fontSize: 14, fontWeight: '600', color: '#475569' },
  saveButton: { backgroundColor: '#2563EB', borderRadius: 8 },
  saveButtonDisabled: { backgroundColor: '#94A3B8' },
  saveButtonText: { color: '#FFFFFF', fontSize: 14, fontWeight: '600', paddingHorizontal: 12, paddingVertical: 6 },
  headerTitle: { fontSize: 18, fontWeight: '600', color: '#0F172A' },
  scroll: { flex: 1 },
  scrollContent: { padding: 20 },
  section: { marginBottom: 20 },
  sectionTitle: { fontSize: 16, fontWeight: '600', color: '#0F172A', marginBottom: 8 },
  sectionSubtitle: { fontSize: 15, fontWeight: '600', color: '#1E293B', marginBottom: 8 },
  tagGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  tagChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
  },
  selectedChip: { backgroundColor: '#2563EB', borderColor: '#2563EB' },
  unselectedChip: { backgroundColor: '#FFFFFF', borderColor: '#CBD5F5' },
  selectedChipText: { color: '#FFFFFF', fontSize: 13, fontWeight: '600', marginRight: 6 },
  unselectedChipText: { color: '#334155', fontSize: 13, fontWeight: '500' },
  removeIcon: { color: '#FFFFFF', fontSize: 16, lineHeight: 16 },
  emptyText: { fontSize: 13, color: '#94A3B8', fontStyle: 'italic' },
  customTagRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    paddingHorizontal: 12,
    paddingVertical: 6,
    gap: 8,
  },
  textInput: { flex: 1, fontSize: 14, color: '#0F172A' },
  addButton: { width: 32, height: 32, borderRadius: 16, backgroundColor: '#E0F2FE', alignItems: 'center', justifyContent: 'center' },
  addButtonDisabled: { backgroundColor: '#E2E8F0' },
  addButtonText: { color: '#1D4ED8', fontSize: 18, marginTop: -2 },
  helpText: { fontSize: 13, color: '#64748B', lineHeight: 18 },
});

export default FriendsModule;
